import { Crown, Sparkle, Award, ShieldCheck } from "lucide-react";
import { LEVELS } from "@/lib/sparks";

export default function LevelHero({ balance, user }) {
  const levelId = balance?.level_id || "fundador";
  const idx = LEVELS.findIndex((l) => l.id === levelId);
  const current = LEVELS[idx] || LEVELS[0];
  const Icon = current.icon;
  const progress = balance?.progress_pct ?? 0;
  const sparksToNext = balance?.sparks_to_next ?? 0;
  const nextName = balance?.next_level_name;

  return (
    <section
      data-testid="level-hero"
      className="relative overflow-hidden rounded-2xl border hairline bg-ink text-cream mb-6"
    >
      <div className="grain absolute inset-0 opacity-25 pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{ background: `radial-gradient(800px 400px at 20% 0%, ${current.accent} 0%, transparent 60%)` }}
      />

      <div className="relative grid grid-cols-12 gap-6 p-6 md:p-10">
        {/* Avatar + identity */}
        <div className="col-span-12 md:col-span-7 flex items-end gap-5">
          <div className="relative shrink-0">
            <img src={user?.avatar} alt="" className="w-24 h-24 md:w-28 md:h-28 rounded-2xl ring-2 ring-cream/20 object-cover" />
            <span
              className="absolute -bottom-2 -right-2 w-12 h-12 rounded-xl flex items-center justify-center ring-2 ring-ink"
              style={{ backgroundColor: current.accent }}
              data-testid="level-icon"
            >
              <Icon className="w-5 h-5 text-cream" strokeWidth={1.8} />
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: current.accent }}>
              ◊ Nivel actual
            </p>
            <h1 data-testid="level-name" className="mt-2 font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95]">
              {current.name}
            </h1>
            <div className="mt-2 flex items-center gap-2 text-sm text-cream/75">
              <span>{user?.name}</span>
              {user?.verified && <ShieldCheck className="w-3.5 h-3.5 text-coral" />}
              <span>·</span>
              <span data-testid="level-mult" className="font-mono text-xs text-coral">{balance?.earn_mult ?? 1}× multiplicador</span>
            </div>
          </div>
        </div>

        {/* Sparks counter */}
        <div className="col-span-12 md:col-span-5 md:text-right flex md:flex-col gap-2 md:items-end items-baseline justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/55">Tus Sparks</p>
          <p data-testid="hero-sparks" className="font-serif text-6xl md:text-7xl tracking-tighter leading-none text-coral">
            {(balance?.total_sparks ?? 0).toLocaleString("es")} <span className="text-3xl md:text-4xl text-cream/85">✦</span>
          </p>
        </div>

        {/* Progress bar */}
        <div className="col-span-12 mt-4">
          {nextName ? (
            <div data-testid="progress-block">
              <div className="flex items-center justify-between mb-2 text-xs">
                <span className="font-mono uppercase tracking-[0.25em] text-cream/55">
                  Progreso al siguiente nivel
                </span>
                <span className="font-mono text-cream/85">
                  Faltan <span className="text-coral">{sparksToNext.toLocaleString("es")}</span> ✦ para {nextName}
                </span>
              </div>
              <div className="h-2 rounded-full bg-cream/10 overflow-hidden">
                <div
                  data-testid="progress-bar"
                  className="h-full bg-gradient-to-r from-coral to-coral-hover transition-all duration-1000 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-1 font-mono text-[10px] text-cream/60">{progress}% completado</div>
            </div>
          ) : (
            <div data-testid="max-level" className="border border-cream/15 rounded-xl bg-cream/[0.04] p-4 text-center">
              <p className="font-serif text-2xl">Has alcanzado el nivel máximo. Eres leyenda Ovamx.</p>
            </div>
          )}
        </div>

        {/* Perks list */}
        <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-cream/10 rounded-xl overflow-hidden border border-cream/10 mt-3">
          {(balance?.perks || current?.perks || []).map((p, i) => (
            <div key={i} data-testid={`perk-${i}`} className="bg-ink p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral mb-1">Beneficio {String(i + 1).padStart(2, "0")}</p>
              <p className="text-sm text-cream/90 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>

        {/* Badges */}
        {balance?.badges?.length > 0 && (
          <div className="col-span-12 mt-2 flex flex-wrap gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/55 self-center mr-2">
              Insignias:
            </span>
            {balance.badges.map((b) => (
              <span key={b} data-testid={`badge-${b.replace(/\s+/g, "-")}`} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cream/10 text-cream text-xs">
                <Sparkle className="w-3 h-3 text-coral" />
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
